import ReconnectingWebSocket from 'reconnecting-websocket';
import { words } from 'globalState';
import { getAddressFromKey, signWithKey } from './keysAndSigning';
import { devLog } from 'utils';

const LIVE_TEXT_CONNECTION_MATCHING_ID_PREFIX = 'liveText:';

function makeLiveTextConnectionMatchingId() {
  return `${LIVE_TEXT_CONNECTION_MATCHING_ID_PREFIX}${crypto.randomUUID()}`;
}

function makeLiveTextSignatureMessage({ addressMain, connectionMatchingId, clientTime, signatureVersion }) {
  return `I want to receive the Knights of Monadom live of ${addressMain} as ${connectionMatchingId}. (epoch time: ${clientTime}, signature version: ${signatureVersion})`;
}

function buildLiveTextUrl({ addressMain, addressOperator, clientTime, signatureVersion, signature, connectionMatchingId }) {
  return `https://${process.env.REACT_APP_KOM_EXTENSION_URL}livetext?addressmain=${addressMain}&addressoperator=${addressOperator}&clienttime=${clientTime}&signatureversion=${signatureVersion}&connectionmatchingid=${encodeURIComponent(connectionMatchingId)}&signature=${encodeURIComponent(signature)}`;
}

// ライブテキスト署名に使う鍵ペアを、グローバルステートから解決する。
// addressOperator が入力されていればその鍵、未入力なら addressMain の鍵。どちらも無ければ undefined。
function resolveKeyPairForLiveText({ keyPairs, addressOperator, addressMain }) {
  const now = Date.now();
  let addressToResolve;

  if (addressOperator !== undefined && addressOperator !== null && addressOperator !== '') {
    addressToResolve = addressOperator;
  }
  else {
    addressToResolve = addressMain;
  }

  if (addressToResolve === undefined || addressToResolve === null || addressToResolve === '') {
    return undefined;
  }

  const keyPairData = keyPairs[addressToResolve];

  if (keyPairData === undefined || keyPairData === null) {
    return undefined;
  }

  if (!(keyPairData.expiration > now)) {
    return undefined;
  }

  if (keyPairData.keyPair === undefined || keyPairData.keyPair === null) {
    return undefined;
  }

  return keyPairData.keyPair;
}

// MAKE LIVE TEXT URL FOR A BROADCASTER
async function makeLiveTextUrlForABroadcaster(state, dispatch) {
  const clientTime = new Date();
  const signatureVersion = state.config.clientParameters.signatureVersion.makeUrlToBroadcast;
  const addressMain = state.makeUrlToBroadcast.addressMain;
  const connectionMatchingId = makeLiveTextConnectionMatchingId();
  const keyPair = resolveKeyPairForLiveText({
    keyPairs: state.keyPairs,
    addressOperator: state.makeUrlToBroadcast.addressOperator,
    addressMain,
  });
  let addressOperator;
  let signature;

  if (keyPair !== undefined && keyPair !== null) {
    let result;

    result = getAddressFromKey(state, dispatch, keyPair, state.networkMona);

    if (result.status === 'fulfilled') {
      addressOperator = result.address;
    }
    else {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToGetTheAddress[state.language] });
      return { status: 'rejected' };
    }

    const message = makeLiveTextSignatureMessage({
      addressMain,
      connectionMatchingId,
      clientTime: clientTime.getTime(),
      signatureVersion,
    });

    result = signWithKey(state, dispatch, keyPair, message, state.networkMona);

    if (result.status === 'fulfilled') {
      signature = result.signature;
    }
    else {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[state.language] });
      return 'rejected';
    }
  }
  else {
    let result;

    result = await window.mpurse.getAddress()
    .then( (result) => {
      addressOperator = result;
      return 'fulfilled';
    })
    .catch( (error) => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.theAddressInYourWalletCanNotBeGotten[state.language] });
      return 'rejected';
    });

    if (result === 'rejected') {
      return { status: 'rejected' };
    }

    const message = makeLiveTextSignatureMessage({
      addressMain,
      connectionMatchingId,
      clientTime: clientTime.getTime(),
      signatureVersion,
    });

    // 署名
    result = await window.mpurse.signMessage(message)
    .then( result => {
      signature = result;
      return 'fulfilled';
    })
    .catch( err => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[state.language] });
      return 'rejected';
    });

    if (result === 'rejected') {
      return { status: 'rejected' };
    }
  }

  dispatch({ type: 'setStateMultiLayers', keys: ['makeUrlToBroadcast', 'addressOperator'], value: addressOperator });
  dispatch({ type: 'setStateMultiLayers', keys: ['makeUrlToBroadcast', 'signature'], value: signature });
  dispatch({ type: 'setStateMultiLayers', keys: ['makeUrlToBroadcast', 'clientTime'], value: clientTime.getTime() });
  dispatch({ type: 'setStateMultiLayers', keys: ['makeUrlToBroadcast', 'connectionMatchingId'], value: connectionMatchingId });

  return { status: 'fulfilled' };
}

// DISCONNECT CONNECTION FOR A BROADCASTER
async function disconnectConnectionForABroadcaster(state, dispatch) {
  const clientTime = new Date();
  const signatureVersion = state.config.clientParameters.signatureVersion.makeUrlToBroadcast;
  const addressMain = state.makeUrlToBroadcast.addressMain;
  const connectionMatchingId = state.makeUrlToBroadcast.connectionMatchingId;
  const keyPair = resolveKeyPairForLiveText({
    keyPairs: state.keyPairs,
    addressOperator: state.makeUrlToBroadcast.addressOperator,
    addressMain,
  });
  let addressOperator;
  let signature;

  // メモリが消えて、connectionMatchingIdがわからなくなると、 disconnect し、 connection テーブルから connectionId を削除することができなくなる。
  // その後、 ごみ connectionId が消えるのは次のタイミング。
  // - 次の duel 送信で GoneException になれば deleteLiveTextConnectionIfGone が消す。
  // - login 時のごみ処理。

  if (connectionMatchingId === undefined || connectionMatchingId === null || connectionMatchingId === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[state.language] });
    return { status: 'rejected' };
  }

  if (keyPair !== undefined && keyPair !== null) {
    let result;

    result = getAddressFromKey(state, dispatch, keyPair, state.networkMona);

    if (result.status === 'fulfilled') {
      addressOperator = result.address;
    }
    else {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToGetTheAddress[state.language] });
      return { status: 'rejected' };
    }

    const message = makeLiveTextSignatureMessage({
      addressMain,
      connectionMatchingId,
      clientTime: clientTime.getTime(),
      signatureVersion,
    });

    result = signWithKey(state, dispatch, keyPair, message, state.networkMona);

    if (result.status === 'fulfilled') {
      signature = result.signature;
    }
    else {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[state.language] });
      return { status: 'rejected' };
    }
  }
  else {
    let result;

    result = await window.mpurse.getAddress()
    .then( (result) => {
      addressOperator = result;
      return 'fulfilled';
    })
    .catch( (error) => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.theAddressInYourWalletCanNotBeGotten[state.language] });
      return 'rejected';
    });

    if (result === 'rejected') {
      return { status: 'rejected' };
    }

    const message = makeLiveTextSignatureMessage({
      addressMain,
      connectionMatchingId,
      clientTime: clientTime.getTime(),
      signatureVersion,
    });

    // 署名
    result = await window.mpurse.signMessage(message)
    .then( result => {
      signature = result;
      return 'fulfilled';
    })
    .catch( err => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[state.language] });
      return 'rejected';
    });

    if (result === 'rejected') {
      return { status: 'rejected' };
    }
  }


  const websocketClient = new ReconnectingWebSocket('wss://' + process.env.REACT_APP_KOM_WEBSOCKET_DOMAIN);

  const requestBody = {
    action: 'connectToBroadcaster',
    subAction: 'disconnect',
    addressMain: addressMain,
    addressOperator: addressOperator,
    clientTime: clientTime.getTime(),
    signatureVersion: signatureVersion,
    signature: signature,
    connectionMatchingId: connectionMatchingId,
  };

  const onOpen = () => {
    websocketClient?.send(JSON.stringify(requestBody));
  };

  websocketClient.addEventListener('open', onOpen);

  const onMessage = (event) => {
    try {
      const data = JSON.parse(event.data);

      if (data.applicationStatus === '200') {
        dispatch({ type: 'setNotification', key: 'notification', value: words.success[state.language] });
      }
      else {
        dispatch({ type: 'setNotification', key: 'notification', value: data.applicationMessage });
      }
    }
    catch (error) {
      // エラー通知
      devLog('irregular WebSocket data.', error);
    }

    websocketClient?.close();
    devLog('websocket closed.');
  };

  websocketClient.addEventListener('message', onMessage);
}

export {
  makeLiveTextUrlForABroadcaster,
  disconnectConnectionForABroadcaster,
  buildLiveTextUrl,
  makeLiveTextSignatureMessage,
};
